import { Box, Button, Flex, Link, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import type { VehicleValuationModel } from 'brokertslib/src/dtos'
import type { OneVehicleIdentificationDto, VehiclesIdentificationDto, VehicleValuationDto } from 'brokertslib/src/dtos/VinOnline'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useState } from 'react'
import { useVehicleValuation } from '../hooks/useVehicle'

import { formatDate, formatPrice } from 'BrokerTsLib/src/Utils/formatPrice'
import { Card, CardBody } from './Card'

interface VehicleEvaluationProps {
  vehiclesIdentification: VehiclesIdentificationDto
  onApplyEvaluation: ({ price, withTax }: { price?: number; withTax?: boolean }) => void
}

export const VehicleEvaluation = ({
  vehiclesIdentification,
  onApplyEvaluation,
}: VehicleEvaluationProps) => {

  const {
    getValues,
    setValue
  } = useFormContext<InsuranceParamsDto>()

  const getValuationParams = (selectedVehicle: OneVehicleIdentificationDto): VehicleValuationModel => {
    const vehicle = getValues('vehicle')
    const generalParams = getValues('generalParams')

    return {
      paramsId: generalParams.paramsId?.toString(),
      licensePlate: vehicle.licensePlate ?? '',
      vin: vehicle.vin ?? '',
      vehicleId: selectedVehicle?.id ?? '',
      firstRegistrationDate: formatDate(vehicle.firstRegistrationDate!),
      accSelected: selectedVehicle?.accessoriesOptionalSelected,
      mileage: vehicle.mileage
    }
  }

  const [vehicleValuation, setVehicleValuation] = useState<VehicleValuationDto | null>(null)

  const { mutateAsync: refetchValuation, isPending: isLoadingValuation } = useVehicleValuation()

  const valuationError = !!vehicleValuation?.error

  return (
    <Box sx={{ display: 'grid', gap: 4, mt: 3 }}>
      <Table variant="striped" sx={{ fontSize: 'sm' }}>
        <Thead>
          <Tr>
            <Th>Popis</Th>
            <Th>V prodeji od</Th>
            <Th>V prodeji do</Th>
            <Th>Cena nového včetně DPH</Th>
          </Tr>
        </Thead>

        <Tbody>
          {vehiclesIdentification && vehiclesIdentification.vehicles && vehiclesIdentification.vehicles.length > 0 && vehiclesIdentification.vehicles?.map(vehicle =>
          (
            <Tr key={vehicle.id}>
              <Td>
                <Text>
                  {vehicle.description}
                  {' '}
                  {vehicle.pdfLink && (
                    <Link variant="underline" href={vehicle.pdfLink} target="_blank">
                      PDF
                    </Link>
                  )}
                </Text>
              </Td>
              <Td>
                {vehicle.saleFrom && formatDate(new Date(vehicle.saleFrom), 'd.M.yyyy')}
              </Td>
              <Td>
                {vehicle.saleTo && formatDate(new Date(vehicle.saleTo), 'd.M.yyyy')}
              </Td>
              <Td>
                <Flex sx={{ flexWrap: 'wrap', gap: 4, placeItems: 'center' }}>
                  {vehicle.priceWithEquipment && <Text>{formatPrice({ price: vehicle.priceWithEquipment })}</Text>}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    isLoading={isLoadingValuation}
                    onClick={() => {
                      const valuationParams = getValuationParams(vehicle)

                      refetchValuation(valuationParams)
                        .then(({ data }) => {
                          setVehicleValuation(data)
                          setValue('vehicle.cebicatVehicleId', valuationParams.vehicleId)
                        })
                    }}
                  >
                    Ocenit
                  </Button>
                </Flex>
              </Td>
            </Tr>
          )
          )}
        </Tbody>
      </Table>

      {
        vehicleValuation && (
          <Card sx={{ fontSize: 'sm' }}>
            <CardBody sx={{ p: 4 }}>
              <Flex sx={{ placeItems: 'center', placeContent: 'space-between' }}>
                {!valuationError &&
                  <>
                    <Text as="span">Obvyklá cena ojetého vozidla</Text>

                    <Flex sx={{ flexWrap: 'wrap', gap: 2 }}>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        colorScheme="blue"
                        onClick={() => onApplyEvaluation({ price: vehicleValuation.vehicleMarketPrice, withTax: false })}
                      >
                        <Text as="span" sx={{ fontWeight: 'bold' }}>
                          {formatPrice({ price: vehicleValuation.vehicleMarketPrice })}
                        </Text>
                        bez DPH
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        colorScheme="green"
                        onClick={() => onApplyEvaluation({ price: vehicleValuation.vehicleMarketPriceWithTax, withTax: true })}
                      >
                        <Text as="span" sx={{ fontWeight: 'bold' }}>
                          {formatPrice({ price: vehicleValuation.vehicleMarketPriceWithTax })}
                        </Text>
                        s DPH
                      </Button>
                    </Flex>
                  </>
                }
                {valuationError &&
                  <Text as="span">{vehicleValuation.error}</Text>
                }
              </Flex>
            </CardBody>
          </Card>
        )
      }
    </Box >
  )
}
